import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  clearOastSessionEvents, createOastSession, deleteOastSession, listOastSessionEvents, listOastSessions, rotateOastSession
} from 'api/oast';
import {
  alpha, Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, InputAdornment, Snackbar,
  Stack, TextField, Tooltip, Typography
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { buildFeed, causeOf, eventCount, explain, isManual } from './oastLogic';
import { GREEN, MONO, ago } from './scanUi';

const SHOW_STEP = 20;
const METHOD_COLOR = { GET: '#38bdf8', POST: '#34d399', PUT: '#fcd34d', DELETE: '#fca5a5' };

export default function OastMonitorPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [range, setRange] = useState('all');
  const [shown, setShown] = useState(SHOW_STEP);
  const [confirm, setConfirm] = useState(null); // { type: 'delete' | 'clear', session }
  const [snack, setSnack] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await listOastSessions();
      const list = data?.results || data || [];
      setSessions(list);
      // Only addresses that were called have events to fetch.
      const hit = list.filter((s) => Number(s.callbacks_count || 0) > 0);
      const pairs = await Promise.all(hit.map(async (s) => {
        try { const e = await listOastSessionEvents(s.id); return [s.id, Array.isArray(e) ? e : e?.results || []]; } catch { return [s.id, []]; }
      }));
      setEvents(Object.fromEntries(pairs));
    } catch (e) { setError(e?.message || 'Failed to load OAST data.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]); // live: a callback can arrive at any time

  const feedAll = useMemo(() => buildFeed(sessions, events), [sessions, events]);
  const hourAgo = Date.now() - 3600000;
  const feed = feedAll.filter((e) => range === 'all' || new Date(e.created_at).getTime() >= hourAgo);
  const hitSessions = sessions.filter((s) => Number(s.callbacks_count || 0) > 0);
  const explanation = explain(feedAll);
  const mine = sessions.find((s) => isManual(s) && s.is_active) || null;
  const q = query.trim().toLowerCase();
  const list = sessions.filter((s) => !q || `${s.label} ${s.token} ${causeOf(s)}`.toLowerCase().includes(q))
    .sort((a, b) => Number(b.callbacks_count || 0) - Number(a.callbacks_count || 0));

  const copy = async (text) => { try { await navigator.clipboard.writeText(text); setSnack('Copied.'); } catch { setSnack('Could not copy.'); } };
  const act = async (fn, okMessage) => {
    setBusy(true); setError('');
    try { await fn(); setSnack(okMessage); await load(); }
    catch (e) { setError(e?.message || 'That did not work.'); }
    finally { setBusy(false); }
  };
  const create = () => act(() => createOastSession({ label: `Manual ${new Date().toISOString().slice(0, 19).replace('T', ' ')}`, metadata: { source: 'manual' } }), 'New address created.');
  const doConfirm = async () => {
    const { type, session } = confirm;
    await act(() => (type === 'delete' ? deleteOastSession(session.id) : clearOastSessionEvents(session.id)), type === 'delete' ? 'Address deleted.' : 'Callbacks cleared.');
    setConfirm(null);
  };

  const card = { border: 1, borderColor: 'divider', borderRadius: 4, bgcolor: 'background.paper' };
  const stat = (label, value, color) => (
    <Box key={label}><Typography sx={{ fontSize: 26, fontWeight: 800, fontFamily: MONO, color }}>{value}</Typography><Typography variant="caption" color="text.secondary">{label}</Typography></Box>
  );

  return (
    <Box className="fuzzscan-page" sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>OAST monitor</Typography>
          <Typography variant="body2" color="text.secondary">Every time a target called one of your addresses, newest first</Typography>
        </Box>
        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
          <Stack direction="row" alignItems="center" gap={1}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: GREEN }} /><Typography variant="body2" sx={{ color: '#4ade80' }}>Live, refreshing every 5 seconds</Typography></Stack>
          <Button variant="contained" disabled={busy} onClick={create} sx={{ minHeight: 44, px: 3, bgcolor: GREEN, color: '#06220F', fontWeight: 700, '&:hover': { bgcolor: '#16a34a' } }}>New callback address</Button>
        </Stack>
      </Stack>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ ...card, p: 2.5, mb: 3 }}>
        <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>Your own address, ready to paste into a test</Typography>
            {mine ? (
              <Stack direction="row" alignItems="center" gap={1.2} sx={{ mt: 0.8 }}>
                <Box sx={{ fontFamily: MONO, fontSize: 14, bgcolor: 'action.hover', borderRadius: 2, px: 1.8, py: 1, wordBreak: 'break-all' }}>{mine.callback_url}</Box>
                <Button size="small" onClick={() => copy(mine.callback_url)} sx={{ color: GREEN, fontWeight: 700 }}>Copy</Button>
              </Stack>
            ) : <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>You have no address of your own yet. Create one with the button above.</Typography>}
          </Box>
          <Stack direction="row" gap={3.5}>
            {stat('Callbacks', eventCount(sessions), eventCount(sessions) ? '#fca5a5' : 'inherit')}
            {stat('Addresses hit', hitSessions.length, 'inherit')}
            {stat('Quiet', sessions.length - hitSessions.length, '#4ade80')}
          </Stack>
        </Stack>
      </Box>

      {explanation && (
        <Box sx={{ borderRadius: 4, p: 2.5, mb: 3, border: 1, borderColor: alpha('#ef4444', 0.4), bgcolor: alpha('#ef4444', 0.07) }}>
          <Stack direction="row" gap={2} alignItems="flex-start" flexWrap="wrap">
            <Chip size="small" label="WHAT THIS MEANS" sx={{ fontWeight: 700, bgcolor: alpha('#ef4444', 0.2), color: '#fca5a5' }} />
            <Typography sx={{ flex: 1, minWidth: 240, lineHeight: 1.7 }}>{explanation}</Typography>
          </Stack>
        </Box>
      )}

      <Stack direction={{ xs: 'column', lg: 'row' }} gap={2.5} alignItems="flex-start">
        <Box sx={{ ...card, flex: '2 1 600px', minWidth: 0, width: '100%', overflow: 'hidden' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Callbacks</Typography>
            <Stack direction="row" gap={0.8}>
              {[['all', `All ${feedAll.length}`], ['hour', `Last hour ${feedAll.filter((e) => new Date(e.created_at).getTime() >= hourAgo).length}`]].map(([id, label]) => (
                <Chip key={id} clickable label={label} onClick={() => { setRange(id); setShown(SHOW_STEP); }}
                  sx={{ fontWeight: 600, border: 1, borderColor: range === id ? alpha(GREEN, 0.5) : 'divider', bgcolor: range === id ? alpha(GREEN, 0.16) : 'transparent', color: range === id ? GREEN : 'text.secondary' }} />
              ))}
            </Stack>
          </Stack>
          {feed.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, pb: 3 }}>
              {loading ? 'Loading…' : feedAll.length ? 'No callbacks in the last hour.' : 'No callbacks yet. When a target contacts one of your addresses, it shows up here within seconds.'}
            </Typography>
          )}
          {feed.slice(0, shown).map((e) => (
            <Stack key={`${e.sessionId}-${e.id}`} direction="row" gap={1.8} sx={{ px: 2.5, py: 1.8, borderTop: 1, borderColor: 'divider', alignItems: 'flex-start' }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444', mt: 0.8, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" gap={1.2} alignItems="center" flexWrap="wrap">
                  <Chip size="small" label={e.method || '-'} sx={{ fontWeight: 700, fontSize: 11, bgcolor: alpha(METHOD_COLOR[e.method] || '#94a3b8', 0.16), color: METHOD_COLOR[e.method] || '#94a3b8' }} />
                  <Typography sx={{ fontFamily: MONO, fontSize: 13.5, fontWeight: 600, wordBreak: 'break-all' }}>{e.path || '/'}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6 }}>From <Box component="span" sx={{ fontFamily: MONO }}>{e.source_ip || 'unknown'}</Box>{e.user_agent ? ` · ${e.user_agent}` : ''}</Typography>
                {e.query_string && <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO, display: 'block', mt: 0.4, wordBreak: 'break-all' }}>{e.query_string}</Typography>}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Caused by: <Box component="span" sx={{ color: 'text.primary' }}>{e.cause}</Box></Typography>
              </Box>
              <Tooltip title={e.created_at ? new Date(e.created_at).toLocaleString() : ''}><Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{ago(e.created_at)}</Typography></Tooltip>
            </Stack>
          ))}
          {feed.length > shown && <Button onClick={() => setShown((n) => n + SHOW_STEP)} sx={{ m: 1.5, color: GREEN, fontWeight: 700 }}>Show more ({feed.length - shown} left)</Button>}
        </Box>

        <Stack gap={2.5} sx={{ flex: '1 1 360px', minWidth: 0, width: '100%' }}>
          <Box sx={{ ...card, p: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Addresses</Typography>
            <TextField size="small" fullWidth value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or token" sx={{ mb: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            {list.length === 0 && <Typography variant="body2" color="text.secondary">{sessions.length ? 'Nothing matches.' : 'No addresses yet.'}</Typography>}
            {list.map((s) => {
              const n = Number(s.callbacks_count || 0);
              return (
                <Box key={s.id} sx={{ py: 1.2, borderTop: 1, borderColor: 'divider' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 600, fontSize: 14 }}>{s.label || `Address ${s.id}`}</Typography><Typography variant="caption" color="text.secondary">{s.is_active ? 'Active' : 'Inactive'} · {causeOf(s)}</Typography></Box>
                    <Typography sx={{ fontFamily: MONO, fontWeight: 800, color: n ? '#fca5a5' : 'text.disabled' }}>{n}</Typography>
                  </Stack>
                  <Stack direction="row" gap={0.5} sx={{ mt: 0.6 }} flexWrap="wrap">
                    <Button size="small" onClick={() => copy(s.callback_url)} sx={{ color: GREEN, minWidth: 0, fontWeight: 600 }}>Copy</Button>
                    <Button size="small" disabled={busy} onClick={() => act(() => rotateOastSession(s.id), 'New address generated.')} sx={{ color: 'text.secondary', minWidth: 0 }}>New address</Button>
                    {n > 0 && <Button size="small" onClick={() => setConfirm({ type: 'clear', session: s })} sx={{ color: 'text.secondary', minWidth: 0 }}>Clear</Button>}
                    {s.scan && <Button size="small" onClick={() => navigate('/web-scans')} sx={{ color: 'text.secondary', minWidth: 0 }}>Open scan</Button>}
                    <Button size="small" color="error" onClick={() => setConfirm({ type: 'delete', session: s })} sx={{ minWidth: 0 }}>Delete</Button>
                  </Stack>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ ...card, p: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>How it works</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>A scan puts one of these addresses inside its test input. If the target later contacts it, even from a hidden background job, the callback shows up here.</Typography>
          </Box>
        </Stack>
      </Stack>

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>{confirm?.type === 'delete' ? 'Delete this address?' : 'Clear its callbacks?'}</DialogTitle>
        <DialogContent><DialogContentText>{confirm?.type === 'delete' ? 'The address stops working and its callbacks are removed. A scan using it will no longer be able to report back.' : 'Every callback recorded for this address is removed. The address keeps working.'}</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={doConfirm} disabled={busy}>{confirm?.type === 'delete' ? 'Delete' : 'Clear'}</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!snack} autoHideDuration={2500} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
